import { motion } from "framer-motion";
import { ShieldCheck, Wallet, TrendingUp, Users, Lock, Crosshair, BarChart3 } from "lucide-react";

const RIGHT_IMG =
  "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxmaW5hbmNpYWwlMjBjaGFydHMlMjBkZXNrfGVufDB8fHx8MTc4NTQxMDIyMHww&ixlib=rb-4.1.0&q=85";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const features = [
  { icon: ShieldCheck, title: "Security First", sub: "Your data. Our priority." },
  { icon: Wallet, title: ">50% Cost Savings", sub: "Enterprise-grade finance at a fraction of the cost." },
  { icon: TrendingUp, title: "Consistent Results", sub: "Reliable today. Stronger tomorrow." },
  { icon: Users, title: "Your Partner in Growth", sub: "More than service — a trusted extension of your team." },
];

const badges = [
  { icon: Lock, l1: "Secure", l2: "By Design" },
  { icon: Crosshair, l1: "Accurate", l2: "By Nature" },
  { icon: Users, l1: "Consistent", l2: "By Commitment" },
  { icon: BarChart3, l1: "Partnership", l2: "For Growth" },
];

export const Hero = () => {
  return (
    <section data-testid="hero-section" className="relative overflow-hidden pt-[88px] bg-gradient-to-br from-[#F7F8FA] to-[#ECEEF2]">
      {/* ---- background layers ---- */}
      <div aria-hidden="true" className="absolute inset-0 z-0 pointer-events-none">
        {/* dotted map texture */}
        <div
          className="absolute top-24 right-0 w-[62%] h-[70%]"
          style={{
            backgroundImage: "radial-gradient(#C3CCDA 1.4px, transparent 1.6px)",
            backgroundSize: "18px 18px",
            opacity: 0.45,
            WebkitMaskImage: "radial-gradient(ellipse at 72% 28%, black 32%, transparent 72%)",
            maskImage: "radial-gradient(ellipse at 72% 28%, black 32%, transparent 72%)",
          }}
        />
        {/* faint upward growth arrow */}
        <svg className="absolute right-[4%] top-[8%] w-[42%] h-[78%] text-[#DBE0E8]" viewBox="0 0 420 300" fill="none" preserveAspectRatio="xMidYMid meet">
          <path d="M15 285 L150 175 L235 215 L400 45" stroke="currentColor" strokeWidth="11" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M338 40 L405 40 L405 107" stroke="currentColor" strokeWidth="11" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {/* faded product photo (desktop) */}
        <div className="hidden lg:block absolute right-0 bottom-0 top-24 w-[30%]">
          <img src={RIGHT_IMG} alt="" className="w-full h-full object-cover object-left" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#EFF1F4] via-[#EFF1F4]/55 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-white/50 via-transparent to-white/40" />
        </div>
      </div>

      {/* ---- content ---- */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pt-14 md:pt-20 pb-14 md:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* left: headline */}
          <div className="lg:col-span-6 xl:col-span-6">
            <motion.p custom={0} variants={fade} initial="hidden" animate="show" className="font-premium italic text-2xl md:text-[1.9rem] text-[#14213D] mb-7">
              Precision for your numbers. Clarity for growth.
            </motion.p>

            <motion.h1 custom={1} variants={fade} initial="hidden" animate="show" className="font-display text-[2.75rem] sm:text-6xl lg:text-[4.4rem] leading-[0.95] text-[#0A0A0A]">
              Security-first accounting services and tax preparation
            </motion.h1>

            <motion.div custom={2} variants={fade} initial="hidden" animate="show" className="mt-8 mb-6 h-[3px] w-40 bg-gradient-to-r from-[#14213D] to-[#3B37E5] rounded-full" />

            <motion.p custom={3} variants={fade} initial="hidden" animate="show" className="font-premium italic text-xl md:text-2xl text-[#14213D] leading-snug max-w-md">
              built on accuracy, consistency, and complete financial confidence.
            </motion.p>
          </div>

          {/* middle: feature list */}
          <div className="lg:col-span-4 xl:col-span-4">
            <ul className="divide-y divide-[#DfE3EA]">
              {features.map((f, i) => {
                const Icon = f.icon;
                return (
                  <motion.li
                    key={f.title}
                    data-testid={`hero-feature-${i}`}
                    custom={i}
                    variants={fade}
                    initial="hidden"
                    animate="show"
                    className="flex items-start gap-4 py-5 first:pt-0"
                  >
                    <span className="shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-white shadow-[0_6px_18px_rgba(20,33,61,0.10)] ring-1 ring-[#EAECF0]">
                      <Icon size={20} className="text-[#14213D]" strokeWidth={2} />
                    </span>
                    <div>
                      <div className="font-semibold text-sm uppercase tracking-[0.06em] text-[#14213D]">{f.title}</div>
                      <div className="text-[#6B7280] mt-1 leading-snug max-w-[26ch]">{f.sub}</div>
                    </div>
                  </motion.li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* bottom trust badges */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-14 md:mt-20 pt-8 border-t border-[#DfE3EA] flex flex-wrap gap-y-6"
        >
          {badges.map((b, i) => {
            const Icon = b.icon;
            return (
              <div
                key={b.l1}
                className={`flex items-center gap-3 pr-8 mr-8 ${i < badges.length - 1 ? "border-r border-[#DfE3EA]" : ""}`}
              >
                <Icon size={22} className="text-[#14213D]" strokeWidth={1.75} />
                <div className="label-tech !text-[#14213D] leading-tight">
                  {b.l1}<br />{b.l2}
                </div>
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
